import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AdminPermissions, AdminUserDto, PlatformRole } from '@eskoolhub/types';
import { useToast } from '../components/Toasts';
import { Chip, Modal, Switch } from '../components/ui';
import { api, ApiRequestError } from '../lib/api';
import { formatDate } from '../lib/format';

const PERMISSION_ROWS: { key: keyof AdminPermissions; label: string; hint: string }[] = [
  { key: 'tickets', label: 'Support tickets', hint: 'Read and work the ticket queue' },
  { key: 'billing', label: 'Billing access', hint: 'Invoices, plans, marking payments received' },
  { key: 'schoolData', label: 'Read-only school data', hint: 'View schools and their data, no changes' },
];

/**
 * Row detail for an admin: change role and permissions, deactivate, or revoke a
 * pending invite. Guarded server-side too — the last active super admin cannot
 * be demoted or switched off.
 */
export function AdminDetailModal({
  admin,
  isSelf,
  onClose,
}: {
  admin: AdminUserDto;
  isSelf: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [role, setRole] = useState<PlatformRole>(admin.role);
  const [permissions, setPermissions] = useState<AdminPermissions>(admin.permissions);

  const dirty =
    role !== admin.role ||
    (role === 'SUPPORT' &&
      PERMISSION_ROWS.some((row) => permissions[row.key] !== admin.permissions[row.key]));

  function afterChange(message: string) {
    void queryClient.invalidateQueries({ queryKey: ['platform-users'] });
    toast.push(message, 'success');
    onClose();
  }

  function onError(error: unknown) {
    toast.push(error instanceof ApiRequestError ? error.message : 'Could not update this admin', 'error');
  }

  const save = useMutation({
    mutationFn: () =>
      api.patch<AdminUserDto>(`/api/platform-users/${admin.id}`, {
        role,
        ...(role === 'SUPPORT' ? { permissions } : {}),
      }),
    onSuccess: () => afterChange(`${admin.name} updated`),
    onError,
  });

  const deactivate = useMutation({
    mutationFn: () => api.post(`/api/platform-users/${admin.id}/deactivate`),
    onSuccess: () => afterChange(`${admin.name} deactivated`),
    onError,
  });

  const reactivate = useMutation({
    mutationFn: () => api.post(`/api/platform-users/${admin.id}/reactivate`),
    onSuccess: () => afterChange(`${admin.name} reactivated`),
    onError,
  });

  const revoke = useMutation({
    mutationFn: () => api.delete(`/api/platform-users/${admin.id}`),
    onSuccess: () => afterChange(`Invite for ${admin.email} revoked`),
    onError,
  });

  const busy = save.isPending || deactivate.isPending || reactivate.isPending || revoke.isPending;
  const isInvite = admin.status === 'INVITED';

  return (
    <Modal
      title={isInvite ? 'Pending invite' : admin.name}
      onClose={onClose}
      footer={
        <>
          {isInvite ? (
            <button className="btn btn--danger" onClick={() => revoke.mutate()} disabled={busy}>
              Revoke invite
            </button>
          ) : admin.status === 'SUSPENDED' ? (
            <button className="btn btn--soft" onClick={() => reactivate.mutate()} disabled={busy}>
              Reactivate
            </button>
          ) : (
            <button
              className="btn btn--danger"
              onClick={() => deactivate.mutate()}
              disabled={busy || isSelf}
              title={isSelf ? 'You cannot deactivate your own account' : undefined}
            >
              Deactivate
            </button>
          )}
          <button className="btn" style={{ marginLeft: 'auto' }} onClick={() => save.mutate()} disabled={busy || !dirty}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          className="avatar"
          style={{
            background: admin.role === 'SUPER_ADMIN' ? 'var(--ink)' : 'var(--ink-soft)',
            color: 'var(--marigold)',
          }}
        >
          {admin.initials}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{admin.name}</span>
          <span className="mono caption">{admin.email}</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {admin.twoFactorEnabled ? (
            <Chip tone="success">2FA on</Chip>
          ) : (
            <Chip tone="warn">2FA not set up</Chip>
          )}
        </div>
      </div>

      <div className="caption">
        {isInvite
          ? `Invited ${formatDate(admin.invitedAt)}${admin.inviteExpiresAt ? ` · expires ${formatDate(admin.inviteExpiresAt)}` : ''}`
          : `Added ${formatDate(admin.createdAt)}`}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>Role</span>
        <div className="segmented">
          <button type="button" aria-pressed={role === 'SUPER_ADMIN'} onClick={() => setRole('SUPER_ADMIN')}>
            Super admin
          </button>
          <button type="button" aria-pressed={role === 'SUPPORT'} onClick={() => setRole('SUPPORT')}>
            Support
          </button>
        </div>
      </div>

      {role === 'SUPPORT' ? (
        <div
          style={{
            background: 'var(--paper)',
            border: '1px solid var(--rule)',
            borderRadius: 16,
            padding: 15,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700 }}>Support permissions</div>
          {PERMISSION_ROWS.map((row) => (
            <div key={row.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 13.5, fontWeight: 700 }}>{row.label}</span>
                <span className="caption">{row.hint}</span>
              </div>
              <Switch
                checked={permissions[row.key]}
                onChange={(next) => setPermissions((current) => ({ ...current, [row.key]: next }))}
                label={row.label}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="banner" style={{ background: 'var(--paper)', color: 'var(--ink-soft)' }}>
          A super admin has every permission. The platform always keeps at least one active super admin — the last
          one cannot be demoted or deactivated.
        </div>
      )}
    </Modal>
  );
}
